import { MetricField as Field, MetricUnit as Unit } from '@/__generated__/graphql';
import { type I18n } from '@lingui/core';
import { getUnit } from './units';

export const getUnitsPerMetric = (metric: Field, i18n: I18n) => {
  const get = (unit: Unit) => {
    const unitAttributes = getUnit(unit, i18n);
    if (!unitAttributes) throw new Error(`Unit ${unit} not specified on getUnit util`);

    return {
      value: unitAttributes.value,
      enumValue: unit,
      label: unitAttributes.short,
    };
  };

  // --- Flow ---
  if ([Field.Flow, Field.FlowRaw, Field.DgaFlow].includes(metric)) {
    return [
      Unit.LiterPerSecond,
      Unit.CubicMeterPerHour,
      Unit.CubicMeterPerSecond,
      Unit.GallonPerMinute,
      Unit.GallonPerHour,
      Unit.GallonPerDay,
      Unit.GallonPerSecond,
      Unit.CubicFootPerMinute,
      Unit.CubicFootPerSecond,
      Unit.AcreFootPerSecond,
    ].map(get);
  }

  // --- Distance / Level ---
  if (
    [
      Field.Level,
      Field.LevelRaw,
      Field.DgaLevel,
      Field.OriginalDistance,
      Field.OriginalDistanceRaw,
      Field.Position,
      Field.SnowLevel,
      Field.SnowLevelRaw,
      Field.SnowLevelNonCalibrated,
      Field.SnowWaterEquivalent,
      Field.SnowWaterEquivalentRaw,
      Field.SnowWaterEquivalentNonCalibrated,
      Field.SnowWaterEquivalentNonCalibratedRaw,
    ].includes(metric)
  ) {
    return [Unit.Millimeter, Unit.Centimeter, Unit.Meter, Unit.Inch, Unit.Foot].map(get);
  }

  // --- Angle ---
  if (
    [
      Field.Angle,
      Field.AngleNonCalibrated,
      Field.AngleNonCalibratedRaw,
      Field.CalculatedAngle,
    ].includes(metric)
  ) {
    return [Unit.Degree, Unit.Radian].map(get);
  }

  // --- Velocity ---
  if (
    [Field.Velocity, Field.VelocityRaw, Field.DopplerVelocity, Field.DopplerVelocityRaw].includes(
      metric,
    )
  ) {
    return [Unit.CentimeterPerSecond, Unit.MillimeterPerSecond, Unit.FootPerSecond].map(get);
  }

  // --- Volume ---
  if (
    [
      Field.VolumeM3,
      Field.VolumeDay,
      Field.VolumeHour,
      Field.VolumeMonth,
      Field.StoredVolume,
      Field.CalculatedVolume,
      Field.CalculatedTotalVolume,
      Field.AccumulatedVol,
      Field.AccumulatedVolRaw,
      Field.OriginalVol,
      Field.OriginalVolRaw,
      Field.AccumulatedVolume,
      Field.DgaAccumulatedVolume,
    ].includes(metric)
  ) {
    return [
      Unit.Liter,
      Unit.CubicMeter,
      Unit.CubicHectometer,
      Unit.Gallon,
      Unit.CubicFoot,
      Unit.AcreFoot,
      Unit.AcreInch,
    ].map(get);
  }

  // --- Temperature ---
  if (
    [
      Field.Temperature,
      Field.TemperatureRaw,
      Field.SnowTemperature,
      Field.SnowTemperatureRaw,
    ].includes(metric)
  ) {
    return [Unit.Celsius, Unit.Fahrenheit].map(get);
  }

  // --- Percentage / Ratio ---
  if (
    [
      Field.BatterySoc,
      Field.BatterySocRaw,
      Field.SignalQuality,
      Field.DissolvedOxygenSaturation,
      Field.DissolvedOxygenSaturationRaw,
    ].includes(metric)
  ) {
    return [Unit.Percent].map(get);
  }

  // --- Conductivity ---
  if ([Field.Conductivity, Field.ConductivityRaw].includes(metric)) {
    return [Unit.MicrosiemensPerCentimeter, Unit.MillisiemensPerCentimeter].map(get);
  }

  // --- pH ---
  if ([Field.Ph, Field.PhRaw].includes(metric)) {
    return [Unit.Ph].map(get);
  }

  // --- Turbidity ---
  if ([Field.TurbidityNtu, Field.TurbidityNtuRaw].includes(metric)) {
    return [Unit.Ntu].map(get);
  }

  if ([Field.TurbidityFnu, Field.TurbidityFnuRaw].includes(metric)) {
    return [Unit.Fnu].map(get);
  }

  // --- Electric Potential (Voltage) ---
  if (
    [
      Field.SolarV,
      Field.SolarVRaw,
      Field.BatteryV,
      Field.BatteryVRaw,
      Field.Orp,
      Field.OrpRaw,
    ].includes(metric)
  ) {
    return [Unit.Volt, Unit.Millivolt].map(get);
  }

  // --- Electric Current ---
  if ([Field.SolarI, Field.SolarIRaw, Field.ChargeI, Field.ChargeIRaw].includes(metric)) {
    return [Unit.Ampere].map(get);
  }

  // --- Electric Power ---
  if ([Field.SolarP, Field.SolarPRaw, Field.ChargeP, Field.ChargePRaw].includes(metric)) {
    return [Unit.Watt].map(get);
  }

  // --- Radiation ---
  if ([Field.SnowRadiation, Field.SnowRadiationRaw].includes(metric)) {
    return [Unit.WattPerSquareMeter].map(get);
  }

  // --- Mass ---
  if ([Field.SnowWeight, Field.SnowWeightRaw].includes(metric)) {
    return [Unit.Kilogram, Unit.Pound].map(get);
  }

  // --- Concentration ---
  if ([Field.DissolvedOxygen, Field.DissolvedOxygenRaw].includes(metric)) {
    return [Unit.MilligramPerLiter].map(get);
  }

  if ([Field.DissolvedOxygenSaturation, Field.DissolvedOxygenSaturationRaw].includes(metric)) {
    return [Unit.Percent].map(get);
  }

  // --- Allocation ---
  if (metric === Field.Allocation) {
    return [
      Unit.LiterPerSecondPerAction,
      Unit.CubicMeterPerSecondPerAction,
      Unit.GallonPerMinutePerAction,
      Unit.CubicFootPerSecondPerAction,
    ].map(get);
  }

  return [];
};
